/**
 * DashboardWebSocket
 * 대시보드 실시간 데이터를 위한 WebSocket 클라이언트
 * - JWT 토큰 기반 인증
 * - 자동 재연결 (지수 백오프)
 * - 메시지 큐 (연결 전 메시지 보관)
 */
import { showToast } from "../utils/show_toast";

const MAX_QUEUE_SIZE = 100;

class DashboardWebSocket {
    constructor(username, getToken, onMessage, onStatusChange, onAuthFailure) {
        this.username = username;
        this.getToken = getToken;   // function that returns current token
        this.onMessage = onMessage;
        this.onStatusChange = onStatusChange;
        this.onAuthFailure = onAuthFailure;

        this.ws = null;
        this.reconnectAttempts = 0;
        // iOS PWA 등에서 사용자가 앱을 보고 있는 동안은 영구적으로 재시도.
        // 상한 대신 30초 backoff cap 으로 트래픽을 제한한다.
        this.maxReconnectAttempts = Infinity;
        this.reconnectDelay = 1000;
        this.maxReconnectDelay = 30000;
        this.reconnectTimer = null;
        this.status = 'disconnected';
        this.messageQueue = [];
        this.shouldReconnect = true;
        // Staleness 감지: 메시지 미수신 시간이 임계값을 넘으면 zombie 소켓으로 간주
        this.lastMessageAt = 0;
        this.staleThresholdMs = 90 * 1000;
        this._lastErrorToastAt = 0;
    }

    connect() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            return;
        }

        // Cleanup any existing connection
        this._cleanup();

        const token = this.getToken();
        if (!token) {
            this._setStatus('disconnected');
            return;
        }
        const wsScheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
        // 개발환경: REACT_APP_WS_HOST 환경변수 사용, 없으면 현재 호스트
        const wsHost = process.env.REACT_APP_WS_HOST || window.location.host;
        const wsUrl = `${wsScheme}://${wsHost}/ws/dashboard/${this.username}/?token=${token}`;

        this.ws = new WebSocket(wsUrl);
        this._setStatus('connecting');

        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            this.lastMessageAt = Date.now();
            this._setStatus('connected');

            // 큐에 있는 메시지 전송
            while (this.messageQueue.length > 0) {
                const msg = this.messageQueue.shift();
                this.ws.send(JSON.stringify(msg));
            }
        };

        this.ws.onmessage = (event) => {
            this.lastMessageAt = Date.now();
            try {
                const data = JSON.parse(event.data);
                this.onMessage(data);
            } catch (error) {
                console.error('[WS] Failed to parse message:', error);
            }
        };

        this.ws.onerror = (error) => {
            console.error('[WS] Error:', error);
            const now = Date.now();
            if (now - this._lastErrorToastAt < 30000) return;
            this._lastErrorToastAt = now;
            showToast({ type: "error", message: "실시간 연결에 문제가 발생했습니다." });
        };

        this.ws.onclose = (event) => {
            this.ws = null;

            // 인증 실패(4001): 재연결 중단 + onAuthFailure 콜백 호출
            if (event.code === 4001) {
                this.shouldReconnect = false;
                this._setStatus('disconnected');
                if (this.onAuthFailure) {
                    this.onAuthFailure();
                }
                return;
            }

            if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
                this._attemptReconnect();
            } else {
                this._setStatus('disconnected');
            }
        };
    }

    disconnect() {
        this._cleanup();
        this._setStatus('disconnected');
    }

    // iOS PWA 포그라운드 복귀, online 이벤트, staleness watchdog 에서 호출.
    // opts.force=true 면 readyState===OPEN 이어도 cleanup 후 재연결 (zombie 소켓 복구).
    forceReconnect(opts = {}) {
        const force = opts.force === true;
        if (!force && this.ws && this.ws.readyState === WebSocket.OPEN) {
            return;
        }
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.reconnectAttempts = 0;
        if (force) {
            this._cleanup();
        }
        this.connect();
    }

    // staleness: 메시지 미수신 임계값 초과 여부
    isStale() {
        if (this.status !== 'connected') return false;
        if (this.lastMessageAt === 0) return false;
        return Date.now() - this.lastMessageAt > this.staleThresholdMs;
    }

    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        } else {
            if (this.messageQueue.length >= MAX_QUEUE_SIZE) {
                this.messageQueue.shift();
                console.warn('[WS] message queue cap hit, oldest dropped');
            }
            this.messageQueue.push(data);
        }
    }

    ping() {
        this.send({ type: 'ping', timestamp: new Date().toISOString() });
    }

    sendRefresh() {
        this.send({ type: 'refresh', timestamp: new Date().toISOString() });
    }

    isConnected() {
        return this.status === 'connected';
    }

    getStatus() {
        return this.status;
    }

    getReconnectAttempts() {
        return this.reconnectAttempts;
    }

    _setStatus(status) {
        this.status = status;
        this.onStatusChange(status, this.reconnectAttempts);
    }

    _attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this._setStatus('disconnected');
            return;
        }

        this._setStatus('reconnecting');
        const delay = Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
        );
        this.reconnectAttempts++;

        this.reconnectTimer = setTimeout(() => {
            this.connect();
        }, delay);
    }

    _cleanup() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.ws) {
            this.ws.onopen = null;
            this.ws.onmessage = null;
            this.ws.onerror = null;
            this.ws.onclose = null;
            this.ws.close();
            this.ws = null;
        }
    }
}

export default DashboardWebSocket;
